import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MobileNav } from "./MobileNav";
import { useAuth } from "@/providers";
import { User, Settings, LogOut, CreditCard, Users, CalendarClock, Ticket, Briefcase, Gift } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { showSupportDialog } from '@/components/support/showSupportDialog';

export function PostAuthNavbar() {
    const { user, logout } = useAuth();
    const { getUserPrimaryTeam } = useAuth();
    const location = useLocation();

    const isActive = (path: string) => location.pathname === `/app/${path}` || location.pathname.startsWith(`/app/${path}/`);
    const getNavLinkClass = (isActive: boolean) => isActive ? "flex items-center gap-2 text-sm font-medium text-primary bg-accent/80 px-3 py-2 rounded-lg shadow-sm transition-all duration-200" : "flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md transition-all duration-200 hover:bg-accent/50 hover:scale-105";

    const isPositionsActive = isActive('positions');
    const isSessionsActive = isActive('sessions');
    const isTeamsActive = isActive('teams');
    return (
        <header className="sticky top-0 z-40 w-full border-b border-border/50 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 shadow-sm">
            <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-10">
                <MobileNav />
                <Logo />
                <nav className="hidden md:flex items-center gap-8 text-sm font-medium ml-12">
                    <Link
                        to="/app/positions"
                        className={getNavLinkClass(isPositionsActive)}
                    >
                        <Briefcase className="size-4" />
                        Positions
                    </Link>
                    <Link
                        to="/app/sessions"
                        className={getNavLinkClass(isSessionsActive)}
                    >
                        <CalendarClock className="size-4" />
                        Sessions
                    </Link>
                    <Link
                        to="/app/teams"
                        className={getNavLinkClass(isTeamsActive)}
                    >
                        <Users className="size-4" />
                        Team
                    </Link>
                </nav>

                <div className="ml-auto flex items-center gap-4">
                    <ThemeToggle />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon" className="shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105" aria-label="User menu">
                                <User className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            <div className="px-3 py-2 text-xs text-muted-foreground font-medium">{user?.email}</div>
                            <DropdownMenuItem asChild>
                                <Link to="/app/settings" className="flex items-center gap-2 cursor-pointer hover:bg-accent/80">
                                    <Settings className="size-4" />
                                    Settings
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                                <Link to="/app/billing" className="flex items-center gap-2 cursor-pointer hover:bg-accent/80">
                                    <CreditCard className="size-4" />
                                    Billing
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => showSupportDialog(user, getUserPrimaryTeam(), user?.email)} className="flex items-center gap-2 cursor-pointer hover:bg-accent/80">
                                <Ticket className="size-4" />
                                Support Ticket
                            </DropdownMenuItem>
                            {user?.billingInfo?.subscriptionTier === 'free' && (
                                <DropdownMenuItem onClick={() => showSupportDialog(user, getUserPrimaryTeam(), user?.email, 'FREE TRIAL REQUEST')} className="flex items-center gap-2 cursor-pointer hover:bg-accent/80">
                                    <Gift className="size-4" />
                                    Request Free Trial
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => logout()} className="flex items-center gap-2 cursor-pointer hover:bg-accent/80 text-destructive focus:bg-destructive/10">
                                <LogOut className="size-4" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
}
